// Kleine Formatierhelfer für die Detailseite. Deutsch, ohne Bibliothek.

const REL = new Intl.RelativeTimeFormat("de-DE", { numeric: "auto" });

/** `null` und `undefined` gleich behandeln (eqeqeq verbietet `== null`). */
const isNil = (v: unknown): v is null | undefined => v === null || v === undefined;

/**
 * „vor 3 Minuten“ / „in 9 Stunden“ / „gerade eben“ / „noch nie abgefragt“.
 * Bewusst ohne Timer: die Seite tickt nicht vor sich hin (Datensparsamkeit),
 * der Wert wird beim nächsten Render neu berechnet.
 */
export function relTime(iso: string | null | undefined): string {
  if (!iso) return "noch nie abgefragt";
  const ms = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(ms)) return "unbekannt";
  return relSeconds(Math.round(ms / 1000));
}

/**
 * Wie {@link relTime}, aber aus einem Abstand in Sekunden: positiv ist
 * Vergangenheit (WG-Handshake-Alter), negativ Zukunft (geplanter Termin).
 *
 * Die Zukunft muss mit: ein Termin morgen früh landete sonst in `secs < 45`
 * und stand als „gerade eben“ auf der Seite „Geplante Updates“ — ein Datum, das
 * gar nicht war, als gerade geschehen gemeldet.
 */
export function relSeconds(secs: number | null | undefined): string {
  if (isNil(secs) || !Number.isFinite(secs)) return "nie";
  const abs = Math.abs(secs);
  if (abs < 45) return secs < 0 ? "gleich" : "gerade eben";
  // Intl zählt umgekehrt: negative Werte sind dort die Vergangenheit.
  const dir = secs < 0 ? 1 : -1;
  if (abs < 3600) return REL.format(dir * Math.round(abs / 60), "minute");
  if (abs < 86_400) return REL.format(dir * Math.round(abs / 3600), "hour");
  return REL.format(dir * Math.round(abs / 86_400), "day");
}

export const fmtDateTime = (iso: string | null | undefined): string =>
  iso ? new Date(iso).toLocaleString("de-DE") : "—";

/** Ortszeit für `<input type="datetime-local">` — der Browser will „lokal ohne
 *  Zone", der Server einen ISO-Zeitpunkt MIT Zone. */
export function toLocalInput(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/** Vorschlag für einen Flash-Termin: nächste Nacht, 03:00 — dann hängt niemand
 *  im Objekt am Netz. */
export function nextNight(): string {
  const date = new Date();
  date.setDate(date.getDate() + 1);
  date.setHours(3, 0, 0, 0);
  return toLocalInput(date);
}

/** Sekunden → „9 T 4 Std 12 Min“. */
export function fmtUptime(secs: number | null | undefined): string {
  if (isNil(secs) || !Number.isFinite(secs)) return "—";
  const d = Math.floor(secs / 86_400);
  const h = Math.floor((secs % 86_400) / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const parts: string[] = [];
  if (d) parts.push(`${d} T`);
  if (d || h) parts.push(`${h} Std`);
  parts.push(`${m} Min`);
  return parts.join(" ");
}

export const fmtKb = (kb: number | null | undefined): string =>
  isNil(kb) ? "—" : kb >= 1024 ? `${(kb / 1024).toFixed(1)} MB` : `${kb} kB`;

export const fmtBytes = (b: number | null | undefined): string => {
  if (isNil(b)) return "—";
  if (b >= 1_000_000) return `${(b / 1_000_000).toFixed(1)} MB`;
  if (b >= 1000) return `${(b / 1000).toFixed(1)} kB`;
  return `${b} B`;
};

/** Belegung in Prozent aus total/free (Speicher, Flash). */
export const usedPct = (total?: number, free?: number): number | null =>
  total && !isNil(free) ? Math.round(((total - free) / total) * 100) : null;

// --- Signalqualität (LTE) ---

export type SignalMetric = "rssi" | "rsrp" | "rsrq" | "sinr";
export type SignalQuality = "good" | "mid" | "bad";

export const SIGNAL_METRICS: SignalMetric[] = ["rssi", "rsrp", "rsrq", "sinr"];

/** Schwellen [gut ab, mittel ab] — Faustwerte aus dem Feldbetrieb. */
const SIGNAL_THRESHOLDS: Record<SignalMetric, [number, number]> = {
  rssi: [-65, -85],
  rsrp: [-90, -105],
  rsrq: [-10, -15],
  sinr: [13, 0],
};

/**
 * Anzeigebereich [Skalenanfang, Skalenende] je Wert. Endet bewusst dort, wo der
 * Wert praktisch nicht mehr vorkommt — sonst klebt der Zeiger immer in der Mitte.
 */
const SIGNAL_RANGE: Record<SignalMetric, [number, number]> = {
  rssi: [-110, -50],
  rsrp: [-125, -70],
  rsrq: [-20, -3],
  sinr: [-10, 30],
};

export const SIGNAL_UNIT: Record<SignalMetric, string> = {
  rssi: "dBm",
  rsrp: "dBm",
  rsrq: "dB",
  sinr: "dB",
};

export const SIGNAL_LABEL: Record<SignalQuality, string> = {
  good: "gut",
  mid: "mittel",
  bad: "schlecht",
};

/** Funkwert einordnen — `null`, wenn der Router den Wert nicht liefert. */
export function rateSignal(metric: SignalMetric, value: number | undefined): SignalQuality | null {
  if (isNil(value)) return null;
  const [good, mid] = SIGNAL_THRESHOLDS[metric];
  if (value >= good) return "good";
  if (value >= mid) return "mid";
  return "bad";
}

export const signalScale = (metric: SignalMetric): [number, number] => SIGNAL_RANGE[metric];

/** Funkwert → 0…100 auf seiner Skala; Basis für Balken und Gesamtwert. */
export function signalPct(metric: SignalMetric, value: number | undefined): number | null {
  if (isNil(value)) return null;
  const [min, max] = SIGNAL_RANGE[metric];
  return Math.round(Math.min(100, Math.max(0, ((value - min) / (max - min)) * 100)));
}

/** Breite der Zonen schlecht/mittel/gut in Prozent — die Farbskala im Balken. */
export function signalZones(metric: SignalMetric): [number, number, number] {
  const [good, mid] = SIGNAL_THRESHOLDS[metric];
  const midPct = signalPct(metric, mid) ?? 0;
  const goodPct = signalPct(metric, good) ?? 100;
  return [midPct, goodPct - midPct, 100 - goodPct];
}

/**
 * Gesamtqualität in Prozent — Mittel der Werte, die der Router liefert.
 * Das Modem meldet keine Prozentzahl; die Zahl ist abgeleitet und taugt zum
 * Vergleich zweier Standorte, nicht als Absolutwert.
 */
export function signalScore(values: Partial<Record<SignalMetric, number>>): number | null {
  const pcts = SIGNAL_METRICS.map((m) => signalPct(m, values[m])).filter(
    (p): p is number => p !== null,
  );
  if (!pcts.length) return null;
  return Math.round(pcts.reduce((a, b) => a + b, 0) / pcts.length);
}

/** Gesamtwert einordnen — dieselben drei Stufen wie die Einzelwerte. */
export const rateScore = (pct: number): SignalQuality =>
  pct >= 60 ? "good" : pct >= 30 ? "mid" : "bad";
